import {
  ArrowButton,
  BaseField,
  DocumentList,
  Select,
  TextArea,
  TextField,
} from "@king-ict/eupisi-ui/components";
import { codeBookToSelectData } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { FormCard } from "../../../components";
import { useAuth } from "../../../hooks/auth/useAuth";
import { SchoolYear } from "../../../models";
import { Request } from "../../../models/Request";
import { Attachment } from "../../../models/Shared/Attachment";
import {
  useGetMzomAppealOutcomeListQuery,
  useGetMzomAppealTypeListQuery,
} from "../../../queries/codeBookQuery";
import { useGetMzomAppealQuery } from "../../../queries/requestQuery";
import { canEditMzomAppeal, getMzomAppealStatusSelectData } from "../utils";
import { getReadOnlySelectProps } from "./utils";

interface MzomAppealCardProps {
  requestId: number;
  item: Request;
  schoolYear?: SchoolYear;
  onDetailsClick: (requestId: number) => void;
  onDocumentClick: (doc: Attachment) => void;
}

export const MzomAppealCard = ({
  requestId,
  item,
  schoolYear,
  onDetailsClick,
  onDocumentClick,
}: MzomAppealCardProps) => {
  const { t } = useTranslation();
  const auth = useAuth();

  const { data: mzomAppeal } = useGetMzomAppealQuery(requestId, {
    skip: !requestId,
  });
  const { data: types } = useGetMzomAppealTypeListQuery();
  const { data: outcomes } = useGetMzomAppealOutcomeListQuery();

  const typeSelectData = codeBookToSelectData(types ?? []);
  const outcomeSelectData = codeBookToSelectData(outcomes ?? []);
  const statusSelectData = getMzomAppealStatusSelectData(t);

  const commonSelectProps = getReadOnlySelectProps(t);

  const canShowDetails =
    mzomAppeal ||
    (canEditMzomAppeal(item, auth) && (!!schoolYear?.isCurrent));

  return (
    <FormCard cardTitle={t("common.requestModel.mzomAppeal")}>
      <Row className="mb-2">
        <Col className="d-flex justify-content-end">
          {canShowDetails && (
            <ArrowButton
              ariaLabel={t("common.details")}
              onClick={() => onDetailsClick(item.id)}
            />
          )}
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Select
            label={t("mzomAppeal.typeLabel")}
            name="typeId"
            value={mzomAppeal?.typeId}
            selectData={typeSelectData}
            {...commonSelectProps}
          />
          <Select
            label={t("mzomAppeal.outcomeLabel")}
            name="outcomeId"
            value={mzomAppeal?.outcomeId}
            selectData={outcomeSelectData}
            {...commonSelectProps}
          />
          <TextField
            label={t("mzomAppeal.classification")}
            name="classification"
            value={mzomAppeal?.classification ?? ""}
            disabled={true}
          />
        </Col>
        <Col md={6}>
          <Select
            label={t("mzomAppeal.statusLabel")}
            name="status"
            value={mzomAppeal?.status}
            selectData={statusSelectData}
            {...commonSelectProps}
          />
          <TextField
            label={t("mzomAppeal.enrolledInstitution")}
            name="enrolledInstitution"
            value={mzomAppeal?.enrolledInstitutionName ?? ""}
            disabled={true}
          />
          <TextField
            label={t("mzomAppeal.associationNumber")}
            name="associationNumber"
            value={mzomAppeal?.associationNumber ?? ""}
            disabled={true}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <TextArea
            label={t("mzomAppeal.note")}
            ariaLabel={t("mzomAppeal.note")}
            readonly={true}
            value={mzomAppeal?.note}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <BaseField label={t("mzomAppeal.responseLabel")}>
            <DocumentList
              documents={mzomAppeal?.attachments ?? []}
              onDocumentClick={onDocumentClick}
              removable={false}
            />
          </BaseField>
        </Col>
      </Row>
    </FormCard>
  );
};
